(() => {
  if (window.CartLineItems) return;

  const LINE_ITEM_SELECTOR = "[data-cart-line-item]";
  /** @type {Map<string, { targetQuantity: number, lastConfirmedQuantity: number, tail: Promise<void> }>} */
  const lineQueues = new Map();

  // Scoped to the surface that owns the control. On the cart page the drawer
  // is in the DOM too, with the same line keys, and a document-wide lookup
  // found the drawer's row first — so the page's row never showed its busy
  // state (PO review, CU-869ev1qza).
  const findLineItem = (lineKey, root = document) =>
    root.querySelector(
      `${LINE_ITEM_SELECTOR}[data-line-key="${CSS.escape(lineKey)}"]`,
    );

  const readLineQuantity = (lineItem) => {
    const parsed = Number(
      lineItem?.querySelector("[data-cart-quantity-value]")?.textContent ?? 1,
    );

    return Number.isFinite(parsed) ? parsed : 1;
  };

  const syncLineItemDisplay = (lineKey, quantity) => {
    for (const lineItem of document.querySelectorAll(
      `${LINE_ITEM_SELECTOR}[data-line-key="${CSS.escape(lineKey)}"]`,
    )) {
      const valueNode = lineItem.querySelector("[data-cart-quantity-value]");

      if (valueNode) {
        valueNode.textContent = String(quantity);
      }
    }
  };

  const getQueueKey = (lineKey, queueScope) => `${queueScope}:${lineKey}`;

  const getOrCreateQueue = (queueKey, initialQuantity) => {
    let queue = lineQueues.get(queueKey);

    if (!queue) {
      queue = {
        targetQuantity: initialQuantity,
        lastConfirmedQuantity: initialQuantity,
        tail: Promise.resolve(),
      };
      lineQueues.set(queueKey, queue);
    }

    return queue;
  };

  const drainLineQueue = (queueKey, lineKey, updateLine, root) => {
    const queue = lineQueues.get(queueKey);
    if (!queue) return Promise.resolve();

    const next = queue.tail
      .catch(() => {})
      .then(async () => {
        while (queue.targetQuantity !== queue.lastConfirmedQuantity) {
          const quantity = queue.targetQuantity;
          const lineItem = findLineItem(lineKey, root);

          if (!lineItem) {
            queue.lastConfirmedQuantity = quantity;
            break;
          }

          let confirmedQuantity = quantity;

          try {
            const result = await updateLine(lineItem, quantity);

            if (typeof result === "number") {
              confirmedQuantity = result;
            } else {
              const refreshedItem = findLineItem(lineKey, root);
              confirmedQuantity = refreshedItem
                ? readLineQuantity(refreshedItem)
                : quantity;
            }
          } catch {
            queue.targetQuantity = queue.lastConfirmedQuantity;
            syncLineItemDisplay(lineKey, queue.lastConfirmedQuantity);
            break;
          }

          queue.lastConfirmedQuantity = confirmedQuantity;

          if (confirmedQuantity !== queue.targetQuantity) {
            queue.targetQuantity = confirmedQuantity;
            syncLineItemDisplay(lineKey, confirmedQuantity);
          }
        }
      });

    queue.tail = next;

    next.finally(() => {
      if (lineQueues.get(queueKey)?.tail === next) {
        lineQueues.delete(queueKey);
      }
    });

    return next;
  };

  const changeQuantity = async (button, delta, updateLine, queueScope) => {
    const lineItem = button.closest(LINE_ITEM_SELECTOR);
    if (!lineItem) return;

    const lineKey = lineItem.dataset.lineKey;
    if (!lineKey || !queueScope) return;

    const root = lineItem.closest("cart-page, cart-drawer") ?? document;

    const queueKey = getQueueKey(lineKey, queueScope);
    const queue = getOrCreateQueue(queueKey, readLineQuantity(lineItem));
    queue.targetQuantity = Math.max(0, queue.targetQuantity + delta);
    syncLineItemDisplay(lineKey, queue.targetQuantity);

    return drainLineQueue(queueKey, lineKey, updateLine, root);
  };

  const removeLine = async (button, updateLine, queueScope) => {
    const lineItem = button.closest(LINE_ITEM_SELECTOR);
    if (!lineItem) return;

    const lineKey = lineItem.dataset.lineKey;
    if (!lineKey || !queueScope) return;

    const root = lineItem.closest("cart-page, cart-drawer") ?? document;

    const queueKey = getQueueKey(lineKey, queueScope);
    const queue = getOrCreateQueue(queueKey, readLineQuantity(lineItem));
    queue.targetQuantity = 0;
    syncLineItemDisplay(lineKey, 0);

    return drainLineQueue(queueKey, lineKey, updateLine, root);
  };

  /**
   * @param {string} lineKey
   * @param {number} quantity
   * @param {string} [sections] - Section id to re-render alongside the change,
   *   returned on `sections` in the response. Omit to get the cart alone.
   */
  const requestLineQuantityChange = (lineKey, quantity, sections) =>
    fetch("/cart/change.js", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        id: lineKey,
        quantity,
        ...(sections ? { sections } : {}),
      }),
    });

  window.CartLineItems = {
    changeQuantity,
    removeLine,
    requestLineQuantityChange,
  };
})();
